/**
 * The exercise card — KANON `Cwiczenia` 17.09.2026, variant **e · Teatr v2**. A lightbox over the
 * list, opened by `?cwiczenie=<id>`.
 *
 * Composition only. `useTheatre` owns the subject, the walk and the similar read;
 * `useExerciseMaterials` owns the materials; every decision either of them composes lives in
 * `model/` or in `entities/exercise-library`.
 *
 * ── `Dialog`, not `AlertDialog` ─────────────────────────────────────────────────────────────
 * Nothing is at stake in reading a card. Escape dismisses, the scrim dismisses, and the editor
 * inside carries its own confirm for the one act that destroys something. The same call
 * `CreatePlanDialog` makes, for the same reason.
 *
 * `finalFocus` is not optional here: this dialog has no `Dialog.Trigger` — a row in the table opens
 * it — so floating-ui has no reference to restore to and focus would land on `<body>` with the next
 * Tab restarting at the top of the document. It is a FUNCTION rather than a ref, so the row is
 * looked up at the moment the card closes: the walk may have changed the subject three times since
 * it opened, and the trainer belongs on the row they ended on. See `useTheatre`.
 *
 * ── The POPUP is the whole viewport, and the card is a child of it ──────────────────────────
 * The canon draws the prev / next squares OUTSIDE the panel, in the margin either side. They cannot
 * be siblings of the popup: a modal Base UI dialog takes everything outside its own subtree out of
 * the tab order and out of the accessibility tree, so a walk rendered beside the card would be
 * drawn, visible and unreachable by any means. And they cannot be `position: fixed` inside the card
 * either — the card carries a transform for its entrance, which makes it the containing block for a
 * fixed child, and its own `overflow: hidden` would then clip them.
 *
 * So `Dialog.Popup` is a transparent, full-viewport stage that holds both, with
 * `pointer-events: none` on it and `auto` on the two things inside — which leaves a press on the
 * empty space reaching the backdrop underneath, so clicking away still dismisses.
 *
 * ── The panel is OPAQUE over a blurred scrim, which is not glass ────────────────────────────
 * `--bg-card` with a hairline and the app's modal shadow, over `--scrim` carrying `--blur-scrim`.
 * The ban is on a glass SURFACE — a translucent tinted panel you can see the page through — and
 * this is the opposite: a neutral blur under a scrim, which is exactly what the day modal already
 * ships and what the owner confirmed on 13 September 2026.
 *
 * ── Nothing inside it moves ─────────────────────────────────────────────────────────────────
 * The panel is a fixed box, the hero takes what the similar strip leaves, and the strip reserves its
 * height before its read lands. The rail scrolls inside itself, so a movement with eleven steps
 * grows downward rather than pushing the picture. The one read that can arrive late is the strip's,
 * and its gate is its own — the rest of the card is on screen from the first frame.
 *
 * ── `open` is the address; the POPUP is drawn from the last movement the card was about ─────
 * The two are not the same question, and treating them as one left the page dead. Base UI decides a
 * closed dialog has finished by waiting on the animations of whatever `popupRef` resolves to, and
 * that wait never calls back when the ref is `null` — so a popup taken out of the tree on the frame
 * the address cleared kept `Dialog.Backdrop` on the page for ever, `data-ending-style` and all,
 * over a shell that stayed `inert`. `useTheatre` holds the drawn subject past the close for exactly
 * this; Base UI then removes the whole portal itself. `CreatePlanDialog` and `DayDock` render their
 * popup unconditionally and never met it.
 *
 * ── The entrance honours the preference, and it does so as a PARAMETER ──────────────────────
 * `screenMotion('swap')` with `reducedMotion` passed in. The CSS block in `tokens/base.css` cannot
 * reach a Motion animation, so a surface that relied on it would ignore the preference while
 * looking compliant.
 */

import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { aliasesIn, heroStillUrl } from '~/entities/exercise-library';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { tabButtonId, tabPanelId } from '~/shared/ui/TabStrip';

import { CreateExerciseDialog } from '~/features/create-exercise';

import type { ExerciseActions } from '../lib/useExerciseActions';
import { useExerciseMaterials } from '../lib/useExerciseMaterials';
import type { TheatreState, TheatreSubject } from '../lib/useTheatre';
import { DEMO_SOURCE, filmSourcesOf, sourceIn } from '../model/theatreSources';

import { DeleteExerciseConfirm } from './DeleteExerciseConfirm';
import { SimilarStrip } from './SimilarStrip';
import { TheatreBar } from './TheatreBar';
import { TheatreHero } from './TheatreHero';
import { TheatreRail } from './TheatreRail';
import { TheatreSources } from './TheatreSources';
import { TheatreYouTube } from './TheatreYouTube';
import { TheatreWalk } from './TheatreWalk';
import styles from './ExerciseTheatre.module.css';

export function ExerciseTheatre({
  theatre,
  actions,
}: {
  readonly theatre: TheatreState;
  /** The creator's acts on an own movement, and the two dialogs that NEST inside this one. */
  readonly actions: ExerciseActions;
}) {
  return (
    <Dialog.Root
      open={theatre.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          // A save's pending landing and a cover failure belong to THIS opening; closing drops them.
          actions.cardClosed();
          theatre.close();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.scrim} />
        {theatre.subject !== null && (
          <TheatreStage theatre={theatre} subject={theatre.subject} actions={actions} />
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The stage: the popup element itself, and it OUTLIVES the movement it is showing.
 *
 * The walk swaps the subject without closing the card, and this element is where the walk listens,
 * where focus lands on open and where Base UI's trap is anchored. Remounting it on every step
 * unmounted a focused dialog: Base UI ran its focus RETURN, which put focus on the row behind —
 * a row a modal dialog has made `inert` — and the next popup pulled it back a beat later. A press
 * inside that window reached an element outside the dialog and was dropped, which is what a held
 * ArrowLeft and two fast presses both look like. So the key sits on the CONTENTS instead, which is
 * where the state that must start again actually lives.
 */
function TheatreStage({
  theatre,
  subject,
  actions,
}: {
  readonly theatre: TheatreState;
  readonly subject: TheatreSubject;
  readonly actions: ExerciseActions;
}) {
  const { t } = useTranslation();
  const popup = useRef<HTMLDivElement>(null);
  // The ⋯ trigger, owned HERE because the card below is keyed and the two nested dialogs are not:
  // they return focus to it when an act raised from its menu is cancelled.
  const more = useRef<HTMLButtonElement>(null);

  return (
    <Dialog.Popup
      ref={popup}
      className={styles.stage}
      // The card's own name, not its title bar's text. The bar used to be the `Dialog.Title`, so
      // the dialog announced itself as „Siłowe · <name> · Dostosuj materiały · Esc" — every control
      // in the band read out as part of a name. See `TheatreBar` for why there is no title element
      // at all now: `aria-labelledby` outranks `aria-label`, so one would shadow this.
      aria-label={t('web:exerciseLibrary.theatre.label', { name: subject.row.name })}
      // The POPUP itself, not its first control. Base UI's default focuses the first tabbable —
      // which here is the walk's „poprzednie" square, a control rather than the card — and the
      // walk below listens on this element, so it would be dead until the trainer Tabbed back out
      // to it. Focusing the popup announces the card and arms its arrows in one act.
      initialFocus={() =>
        // A card a NEW movement's save opened lands on its heading; any other opening on the
        // popup, which is what arms the arrow walk.
        actions.takeCardLanding(subject.detail.id, 'create')
          ? (popup.current?.querySelector<HTMLElement>('[data-theatre-heading]') ?? popup.current)
          : popup.current
      }
      finalFocus={theatre.finalFocus}
      // ONLY when the popup itself has the key. See `useTheatre`: the walk yields to everything
      // inside the card, including the rail's own scroller.
      onKeyDown={theatre.onKeyDown}
    >
      {/* KEYED BY THE MOVEMENT. The walk and the similar strip both swap the subject without
        * closing the card, and everything inside that holds state of its own — the materials
        * editor, a failed photograph's flag — has to start again when they do. A key is one line;
        * reconciling four pieces of state against a changing subject is four bugs. It sits here
        * rather than on the popup for the reason the header gives. */}
      <TheatreCard
        key={subject.detail.id}
        theatre={theatre}
        subject={subject}
        actions={actions}
        moreRef={more}
      />

      {/* NESTED inside this popup's React tree, so Base UI stacks them over the card rather than
        * beside it: Escape answers the top one and leaves the card, and the card's own focus trap
        * does not fight theirs. Rendered unconditionally for the reason `CreateExerciseDialog`
        * gives; the edit and duplicate requests are the only ones this instance draws. */}
      <CreateExerciseDialog
        open={actions.creatorOpen && actions.creatorRequest?.mode !== 'create'}
        request={actions.creatorRequest?.mode === 'create' ? null : actions.creatorRequest}
        onClose={actions.closeCreator}
        onSaved={actions.onCreatorSaved}
        finalFocus={() => {
          // After a saved duplicate the NEW card lands focus on its own heading (see
          // `TheatreCard`), so this leaves focus alone; otherwise the opener while it stands, and a
          // menu-opened wizard cancelled goes back to the ⋯ that raised it.
          const landing = actions.creatorFinalFocus();
          return landing === false ? false : (landing ?? more.current);
        }}
      />
      <DeleteExerciseConfirm actions={actions} finalFocus={more} />
    </Dialog.Popup>
  );
}

/** Everything the card is ABOUT, and the only state a step has to throw away. */
function TheatreCard({
  theatre,
  subject,
  actions,
  moreRef,
}: {
  readonly theatre: TheatreState;
  readonly subject: TheatreSubject;
  readonly actions: ExerciseActions;
  readonly moreRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const reducedMotion = useScreenMotionPreference();
  const contentLang = useContentLanguage();

  const materials = useExerciseMaterials(
    subject.detail.id,
    subject.ownership,
    subject.detail.links,
  );

  // Which picture the hero shows: the demo, or one of the materials that is a YouTube film. Read
  // from the links on screen, so a save adds or drops a tab in place; a key that stops existing is
  // the demo again. The card is keyed on the movement, so a walk always lands on the demo.
  const sources = filmSourcesOf(materials.links);
  const [sourceKey, setSourceKey] = useState(DEMO_SOURCE);
  const film = sourceIn(sources, sourceKey);
  const heroPanel =
    sources.length === 0 ? undefined : { id: tabPanelId(DEMO_SOURCE), tabId: tabButtonId(DEMO_SOURCE) };

  // The strip's OWN gate. The card does not wait as a whole — everything but this arrived with the
  // catalogue — so a shared gate would hold a skeleton over facts that are already on screen.
  const strip = useSkeletonGate(theatre.similar.status === 'pending');

  /* A DUPLICATE's save swaps the open card to the copy, which remounts this component: focus goes
   * to the copy's heading here, because the dialog is already open and no `initialFocus` runs —
   * without it focus fell out of the unmounting old card and the modal handed it to the walk. */
  const { takeCardLanding } = actions;
  const id = subject.detail.id;
  useEffect(() => {
    if (takeCardLanding(id, 'duplicate')) {
      document.querySelector<HTMLElement>('[data-theatre-heading]')?.focus();
    }
  }, [takeCardLanding, id]);

  return (
    <>
      {theatre.neighbours !== null && (
        <TheatreWalk neighbours={theatre.neighbours} onStep={theatre.goTo} />
      )}

      <motion.div className={styles.panel} {...screenMotion('swap', { reducedMotion })}>
        <TheatreBar
          subject={subject}
          onClose={() => {
            actions.cardClosed();
            theatre.close();
          }}
          moreRef={moreRef}
          onEdit={() => {
            actions.openCreator('edit', subject.detail);
          }}
          onDuplicate={() => {
            actions.openCreator('duplicate', subject.detail);
          }}
          onChangeCover={(file) => {
            actions.changeCover(subject.detail.id, file);
          }}
          onDelete={() => {
            actions.askDelete(subject.detail);
          }}
          review={{
            onMark: () => {
              actions.markChecked(subject.detail.id);
            },
            marking: actions.markingChecked,
            checked: actions.checkedId === subject.detail.id,
            failed: actions.checkFailedId === subject.detail.id,
          }}
          onCustomiseMaterials={() => {
            // With a blank row when there is nothing to edit yet, without one when there is: the
            // editor opens on what is already there and puts the caret in its last field.
            materials.edit(materials.links.length === 0);
          }}
        />

        <div className={styles.body}>
          <div className={styles.left}>
            {sources.length > 0 && (
              <TheatreSources
                sources={sources}
                selected={film === null ? DEMO_SOURCE : film.key}
                onSelect={setSourceKey}
                demoStill={heroStillUrl(subject.detail)}
              />
            )}
            {film !== null ? (
              <TheatreYouTube
                key={film.key}
                video={film.video}
                label={film.label}
                url={film.url}
                panelId={tabPanelId(film.key)}
                tabId={tabButtonId(film.key)}
              />
            ) : (
            <TheatreHero
              row={subject.row}
              // The COVER first here, where the row and the panel thumb take the poster first: a
              // 720px poster upscaled into a 636px box on a 2x display reads soft, and
              // `entities/exercise-library` owns both orders. See `heroStillUrl`.
              stillUrl={heroStillUrl(subject.detail)}
              videoUrl={subject.detail.videoUrl}
              mediaNote={actions.mediaNote(subject.detail.id)}
              panel={heroPanel}
            />
            )}
            <SimilarStrip
              // The GATE's wait, not the read's. The strip forks on this three-valued word, so
              // handing it the raw status would swap to the cards the instant `/similar` answered
              // — bars gone in that frame, and the cards already on screen blinking to transparent
              // 300ms later when the gate finally hands out `.reveal`. `waiting` outlives the
              // answer by exactly the bars' minimum life; `error` and `ready` are still the read's.
              status={strip.waiting ? 'pending' : theatre.similar.status}
              cards={theatre.similar.cards}
              skeleton={strip.skeleton}
              reveal={strip.reveal}
              onPick={theatre.goTo}
            />
          </div>

          <TheatreRail
            row={subject.row}
            description={subject.detail.description}
            instructions={subject.detail.instructions}
            measurementFields={subject.detail.measurementFields}
            materials={materials}
            exerciseId={subject.detail.id}
            aliases={aliasesIn(subject.detail, contentLang)}
            tags={subject.detail.tags}
          />
        </div>
      </motion.div>
    </>
  );
}
